import React, { useState } from "react";
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  Copy,
  Download,
  FileText,
  Loader2,
  MessageCircle,
  Percent,
  Printer,
  QrCode,
  Receipt,
  ShieldCheck,
} from "lucide-react";
import { copiar, Btn, Modal, Pill, Placa, Stat, Vazio } from "../ui/componentes";
import { brl, fdata, issDa, pct, regimeDe } from "../ui/formato";

/* ══════════════════════════════════════════════════════════════════
   NOTAS FISCAIS
   ══════════════════════════════════════════════════════════════════ */
function Notas({ db, toast, emitirNF, abrirOS }) {
  const [aba, setAba] = useState("todas");
  const [ver, setVer] = useState(null);
  const lista = db.notas.filter((n) => aba === "todas" || n.tipo === aba);
  const pendentes = db.os.filter((o) => ["finalizada", "entregue"].includes(o.status) && (!o.nfServico || !o.nfPeca));
  const cli = (id) => db.clientes.find((c) => c.id === id);
  const totalMes = db.notas.filter((n) => n.status === "autorizada").reduce((s, n) => s + n.valorServ + n.valorPeca, 0);
  const impostos = db.notas.filter((n) => n.status === "autorizada").reduce((s, n) => s + n.imposto, 0);

  /* Os oito valores do enum, não quatro.
     Faltavam rascunho, enfileirada, denegada e erro_comunicacao, e o acesso
     era direto: `STN[x.status][0]`. Enquanto a lista chegava vazia isso nunca
     aparecia; com nota de verdade, a primeira presa em "enfileirada"
     derrubaria a tela. O `?? padrão` cobre valor novo no enum também. */
  const STN = {
    rascunho:          ["mut",  "Rascunho"],
    enfileirada:       ["warn", "Na fila"],
    processando:       ["warn", "Processando"],
    autorizada:        ["ok",   "Autorizada"],
    rejeitada:         ["crit", "Rejeitada"],
    denegada:          ["crit", "Denegada"],
    cancelada:         ["mut",  "Cancelada"],
    erro_comunicacao:  ["crit", "Falha de comunicação"],
  };
  const sit = (st) => STN[st] ?? ["mut", st];
  const n = ver ? db.notas.find((x) => x.id === ver) : null;

  return (
    <div className="grid gap-4">
      <div className="grid gap-4" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))" }}>
        <Stat label="Notas emitidas" value={db.notas.length} icon={FileText} sub={`${db.notas.filter((x) => x.status === "autorizada").length} autorizadas`} />
        <Stat label="Valor documentado" value={brl(totalMes)} icon={Receipt} />
        <Stat label="Impostos apurados" value={brl(impostos)} icon={Percent} sub={`ISS ${pct(issDa(db.oficina))}% · ${regimeDe(db.oficina)}`} />
        <Stat label="Aguardando emissão" value={pendentes.length} tone={pendentes.length ? "signal" : "ok"} icon={Clock} sub="OS concluídas sem nota" />
      </div>

      {pendentes.length > 0 && (
        <div className="card p-4" style={{ borderLeft: "3px solid var(--signal)" }}>
          <div className="flex items-center gap-2 mb-3"><AlertTriangle size={15} className="t-signal" /><span className="dsp" style={{ fontSize: 15 }}>OS concluídas sem nota fiscal</span></div>
          <div className="grid gap-2">
            {pendentes.map((o) => {
              const subS = o.itens.filter((i) => i.tipo === "servico").reduce((s, i) => s + i.qtd * i.valor, 0);
              const subP = o.itens.filter((i) => i.tipo === "peca").reduce((s, i) => s + i.qtd * i.valor, 0);
              return (
                <div key={o.id} className="flex flex-wrap items-center gap-3 p-3 brd" style={{ borderRadius: "var(--r)" }}>
                  <Placa n={db.veiculos.find((v) => v.id === o.vei)?.placa || ""} />
                  <span className="mono text-sm font-semibold">{o.id}</span>
                  <span className="text-sm flex-1" style={{ minWidth: 120 }}>{cli(o.cli)?.nome}</span>
                  {!o.nfServico && subS > 0 && <Btn variant="o" size="xs" icon={FileText} onClick={() => emitirNF(o, "nfse")}>NFS-e {brl(subS)}</Btn>}
                  {!o.nfPeca && subP > 0 && <Btn variant="o" size="xs" icon={Receipt} onClick={() => emitirNF(o, "nfe")}>NF-e {brl(subP)}</Btn>}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="flex gap-1" style={{ borderBottom: "1px solid var(--line)" }}>
        {[["todas", "Todas"], ["nfse", "NFS-e — serviços"], ["nfe", "NF-e — peças"]].map(([k, l]) => (
          <button key={k} className={`tab ${aba === k ? "on" : ""}`} onClick={() => setAba(k)}>{l} <span className="mono t-mut2">{k === "todas" ? db.notas.length : db.notas.filter((x) => x.tipo === k).length}</span></button>
        ))}
      </div>

      <div className="card" style={{ overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="tbl">
            <thead><tr><th>Número</th><th>Tipo</th><th>Cliente</th><th>OS</th><th>Emissão</th><th>Situação</th><th className="num">Impostos</th><th className="num">Valor</th><th></th></tr></thead>
            <tbody>
              {lista.map((x) => (
                <tr key={x.id} style={{ cursor: "pointer" }} onClick={() => setVer(x.id)}>
                  <td className="mono text-sm font-semibold">{x.id}</td>
                  <td><Pill t={x.tipo === "nfse" ? "info" : "mut"}>{x.tipo === "nfse" ? "NFS-e" : "NF-e"}</Pill></td>
                  <td className="text-sm">{cli(x.cli)?.nome || "—"}</td>
                  <td className="mono text-xs t-mut">{x.os}</td>
                  <td className="text-sm mono t-mut">{fdata(x.data)}</td>
                  <td><Pill t={sit(x.status)[0]}>{sit(x.status)[1]}</Pill></td>
                  <td className="num text-sm t-mut">{brl(x.imposto)}</td>
                  <td className="num text-sm font-semibold">{brl(x.valorServ + x.valorPeca)}</td>
                  <td><ChevronRight size={15} className="t-mut2" /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {lista.length === 0 && <Vazio icon={FileText} titulo="Nenhuma nota nessa aba" msg="As notas aparecem aqui assim que forem emitidas a partir de uma OS concluída." />}
      </div>

      <Modal open={!!n} onClose={() => setVer(null)} w="max-w-2xl" title={n ? `${n.tipo === "nfse" ? "NFS-e" : "NF-e"} ${n.id}` : ""} sub={n ? `Emitida em ${fdata(n.data)} · ${cli(n.cli)?.nome}` : ""}
        footer={n && <>
          {/* XML e DANFE vêm do provedor fiscal junto com a autorização.
              A ligação com ele ainda não está feita, então estes três não
              têm de onde tirar o documento. */}
          <Btn variant="o" icon={Download} onClick={() => toast("Ainda não disponível", "O XML vem do provedor fiscal, e a ligação com ele ainda não está feita.", "alerta")}>Baixar XML</Btn>
          <Btn variant="o" icon={Printer} onClick={() => toast("Ainda não disponível", "A DANFE é gerada pelo provedor fiscal a partir do XML autorizado.", "alerta")}>Imprimir DANFE</Btn>
          <Btn variant="p" icon={MessageCircle} onClick={() => toast("Ainda não disponível", "Enviar a nota depende do XML e do PDF do provedor.", "alerta")}>Enviar ao cliente</Btn>
        </>}>
        {n && (
          <div className="grid gap-4">
            <div className="flex flex-wrap items-center gap-3 p-3.5" style={{ background: n.status === "autorizada" ? "var(--ok-l)" : "var(--warn-l)", border: `1px solid ${n.status === "autorizada" ? "#BFE3D6" : "#EDD9B4"}`, borderRadius: "var(--r)" }}>
              {n.status === "autorizada" ? <ShieldCheck size={19} className="t-ok" /> : <Loader2 size={19} className="t-warn" />}
              <div className="flex-1">
                <div className="text-sm font-semibold">{n.status === "autorizada" ? "Autorizada pela SEFAZ" : "Aguardando retorno da SEFAZ"}</div>
                <div className="text-xs t-mut mono">Protocolo {n.protocolo}</div>
              </div>
              <QrCode size={38} className="t-mut2" />
            </div>
            <div>
              <div className="eyebrow mb-1.5">Chave de acesso</div>
              <div className="flex items-center gap-2">
                <code className="mono text-xs flex-1 p-2.5" style={{ background: "var(--el)", border: "1px solid var(--line)", borderRadius: "var(--r)", wordBreak: "break-all", lineHeight: 1.6 }}>{n.chave}</code>
                <Btn variant="o" size="xs" icon={Copy} onClick={() => copiar(n.chave, toast, "Chave")} />
              </div>
            </div>
            <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))" }}>
              {(() => {
                const base = n.valorServ + n.valorPeca;
                // A alíquota sai do próprio documento, não de constante: é o
                // que a SEFAZ de fato aplicou nesta nota.
                const efetiva = base > 0 ? pct(((n.imposto / base) * 100).toFixed(2)) : null;
                return [
                  ["Base de cálculo", brl(base)],
                  [efetiva ? `${n.tipo === "nfse" ? "ISS" : "ICMS"} ${efetiva}%` : (n.tipo === "nfse" ? "ISS" : "ICMS"),
                   brl(n.imposto)],
                  ["Valor líquido", brl(base - n.imposto)],
                  ["Regime", regimeDe(db.oficina)],
                ];
              })().map(([l, v]) => (
                <div key={l} className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}><div className="eyebrow mb-1">{l}</div><div className="mono font-semibold">{v}</div></div>
              ))}
            </div>
            <div className="bg-el brd p-3" style={{ borderRadius: "var(--r)" }}>
              <div className="eyebrow mb-1">Discriminação</div>
              <div className="text-sm">{n.tipo === "nfse" ? "Prestação de serviços de manutenção e reparação de veículos automotores." : "Venda de peças e acessórios aplicados em veículo automotor."}</div>
              <div className="text-xs t-mut mt-1.5 mono">Ordem de serviço {n.os}</div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export { Notas };
